import { ApolloError } from '@apollo/client'
import * as React from 'react'
import { handleError } from '../../../../common/src/data/errors'
import { addFeedback } from '../../../../common/src/feedback'
import memoize from '../../../../common/src/memoize'
import validate from '../../../../common/src/validate'
import EditBoxLarge from '../components/EditBoxLarge'

export enum Tab {
  EXCHANGE = 'exchangeCreds',
  MSGRAPH = 'msGraphCreds',
  GSUITE = 'gSuiteCreds',
  APPFOLIO = 'apmInfo',
  YARDI = 'yardiInfo',
  RESMAN = 'resmanInfo',
  ENTRATA = 'entrataInfo',
}

interface FirmCredsEditBoxProps {
  firmId: number
  fetchKey: Tab
  creds: string | null
  testid?: string
  fetchOnEdit?: (firmId: number, fetchKey: Tab) => Promise<any>
  mutateOnSubmit: (firmId: number, fetchKey: Tab, jsonValue: string) => Promise<any>
}

interface FirmCredsEditBoxState {
  isEditing: boolean
  hasBeenUpdated: boolean
  // the value of the field we are editing
  editValue: string | null
  error?: string
}

export default class FirmCredsEditBox extends React.PureComponent<FirmCredsEditBoxProps, FirmCredsEditBoxState> {
  private focusHandlers = {}
  private blurHandlers = {}
  private editHandlers = {}

  constructor(props: FirmCredsEditBoxProps) {
    super(props)
    this.state = {
      isEditing: false,
      hasBeenUpdated: false,
      editValue: '{}',
    }
  }

  componentDidUpdate(nextProps: FirmCredsEditBoxProps) {
    if (nextProps.fetchKey !== this.props.fetchKey || nextProps.creds !== this.props.creds) {
      this.setState({
        editValue: this.props.creds,
        isEditing: false,
        error: undefined,
      })
    }
  }

  componentDidMount() {
    window.addEventListener('keydown', this.onWindowKeyDownBound)
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.onWindowKeyDownBound)
  }

  private onWindowKeyDownBound = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this.state.isEditing) {
      this.stopEditing()
    }
  }

  private onFocus() {
    const { isEditing } = this.state

    if (isEditing) {
      return
    }
    const { firmId, fetchKey, fetchOnEdit } = this.props

    if (fetchOnEdit) {
      fetchOnEdit(firmId, fetchKey)
        .then(res => this.setState({ editValue: res, error: undefined }))
        .catch(error => this.setState({ error: error.message }))
        .finally(() => this.setState({ isEditing: true }))
    } else {
      this.setState({ isEditing: true })
    }
  }

  private onBlurBound = () => {
    this.stopEditing()
  }

  private onKeyDownBound = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { isEditing } = this.state
    if (!isEditing) {
      return
    }

    if (e.keyCode === 13 || e.keyCode === 9) {
      // tab or enter
      const { value } = e.currentTarget
      this.submitEdit(value)
    } else if (e.key === 'Escape') {
      this.stopEditing()
    }
    e.stopPropagation()
  }

  private submitEdit(value: string) {
    const { firmId, fetchKey, mutateOnSubmit } = this.props

    const validation = validate.json().validate(value)
    if (validation.error == null && validation.value) {
      const cleanVal = validation.value

      mutateOnSubmit(firmId, fetchKey, cleanVal)
        .then(() => {
          addFeedback(`Saved ${fetchKey} for firm ${firmId}`)
          this.setState({ isEditing: false, hasBeenUpdated: true, editValue: cleanVal })
        })
        .catch((error: ApolloError) => {
          handleError(error)
          this.setState({ error: error.message })
        })
    } else {
      this.setState({ error: validation.error!.message })
    }
  }

  private stopEditing() {
    const { hasBeenUpdated, editValue } = this.state
    const { creds } = this.props
    this.setState({ error: undefined, isEditing: false, editValue: hasBeenUpdated ? editValue : creds })
  }

  render() {
    const { fetchKey, testid } = this.props
    const { isEditing, editValue, error } = this.state

    return (
      <EditBoxLarge
        testid={testid}
        error={error}
        body={editValue || ''}
        canDelete={false}
        canEdit={true}
        isEditing={isEditing}
        key={fetchKey}
        label={fetchKey}
        labelColor={'silver'}
        onKeyDown={this.onKeyDownBound}
        onFocus={memoize(this.focusHandlers, fetchKey, () => () => this.onFocus())}
        onBlur={memoize(this.blurHandlers, fetchKey, () => () => this.onBlurBound())}
        submitEdit={memoize(this.editHandlers, fetchKey, () => (value: string) => this.submitEdit(value))}
      />
    )
  }
}
